import {
  // StrictMode,
  useEffect,
} from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { App, ConfigProvider } from "antd";
import { InMemoryWebStorage, WebStorageStateStore } from "oidc-client-ts";
import { RouterProvider } from "react-router";

import "@ant-design/v5-patch-for-react-19";
import "./i18n";

import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import { useTranslation } from "react-i18next";
import { SWRConfig } from "swr";
import { ErrorBoundary } from "@/components/ErrorBoundary.tsx";
import { FullscreenLoading } from "@/components/LoadingComponent";
import { setupClinicContextMonitor, validateClinicContextOnInit } from "@/utils/clinicContextGuard";
import { logEnvironmentInfo } from "@/utils/environment.ts";
import { Logger } from "@/utils/logger.ts";
import { swrConfig } from "./api/swr-config";
import DebugPanel from "./components/DebugPanel";
import VersionNotification from "./components/VersionNotification";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { EnvVariables } from "./env-variables.ts";
import { useGlobalErrorTracking } from "./hooks/useGlobalErrorTracking";
import { useSessionRefreshTimer } from "./hooks/useSessionRefreshTimer";
import { analytics } from "./lib/analytics";
import { trackInitialNavigationPerformance } from "./lib/analytics/performance";
import { installFetchRequestIdInstrumentation } from "./lib/fetch-request-id";
import { getSentryReactRootOptions, initSentry } from "./lib/sentry";
import type router from "./routers/index.tsx";
import theme from "./theme.ts";
import { cleanupLegacyStorage } from "./utils/cleanupLegacyStorage";
import { configureDayjs } from "./utils/timezone.ts";

// Big.js extensions - extend prototype for JSON serialization
import "./lib/big-extensions";

initSentry();
analytics.initialize();
installFetchRequestIdInstrumentation({
  skipRequestIdHeaderUrls: [EnvVariables.tokenExchangeUrl],
});

// Configure dayjs with timezone support
configureDayjs();

cleanupLegacyStorage();

const legacyUser = localStorage.getItem("user");
if (legacyUser) {
  localStorage.removeItem("user");
}

// Keep the OIDC user (id/access/refresh tokens) in memory instead of the library's default
// sessionStorage (ECHO-2318). The app's own session is restored from `LocalStorage` on reload.
const oidcUserStore = new WebStorageStateStore({ store: new InMemoryWebStorage() });
// Keep the OIDC signin state (nonce, PKCE verifier) in sessionStorage instead of the library's
// default localStorage (ECHO-2318). Cognito redirects back to the same tab.
const oidcStateStore = new WebStorageStateStore({ store: window.sessionStorage });

interface AppContentProps {
  router: typeof router;
}

const EarlyAuthLoading = () => {
  const { isAuthenticated, loading } = useAuth();
  const currentPath = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const isLoginPage = currentPath === "/login";
  const isAdditionalInfoPage = currentPath === "/additional-info";
  const hasCallbackParams =
    urlParams.has("user") ||
    urlParams.has("session") ||
    urlParams.has("iv") ||
    urlParams.has("code") ||
    urlParams.has("state") ||
    urlParams.get("sso") === "true";
  const showEarlyLoading =
    loading && !isAuthenticated && !isLoginPage && !isAdditionalInfoPage && !hasCallbackParams;

  if (!showEarlyLoading) return null;

  return (
    <FullscreenLoading
      message="Redirecting to login..."
      animationType="particles"
      animationSize={70}
      animationColor="#1890ff"
    />
  );
};

const AppContent = ({ router }: AppContentProps) => {
  useSessionRefreshTimer();

  useEffect(() => {
    validateClinicContextOnInit();
    const cleanup = setupClinicContextMonitor();
    return cleanup;
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <DebugPanel />
      <VersionNotification />
    </>
  );
};

interface AppRootProps {
  router: typeof router;
}

const AppRoot = ({ router }: AppRootProps) => {
  const { i18n } = useTranslation();
  useGlobalErrorTracking();

  // Initialize analytics
  useEffect(() => {
    analytics.initialize();
    trackInitialNavigationPerformance();
  }, []);

  Logger.debug("Environment:", { mode: import.meta.env.MODE });
  logEnvironmentInfo();

  const getAntdLocale = () => {
    if (i18n.language.startsWith("zh")) {
      return zhCN;
    }
    return enUS;
  };

  const cognitoAuthConfig = {
    authority: EnvVariables.cognitoDomain,
    client_id: EnvVariables.clientId,
    redirect_uri: window.location.origin,
    response_type: "code",
    scope: "aws.cognito.signin.user.admin email openid phone profile",
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true,
    userStore: oidcUserStore,
    stateStore: oidcStateStore,
    onSigninCallback: () => {
      // AuthConsumer redirects after access token persistence and session initialization.
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };

  return (
    // <StrictMode>
    <ErrorBoundary>
      <SWRConfig value={swrConfig}>
        <ConfigProvider theme={theme} locale={getAntdLocale()}>
          <App>
            <AuthProvider cognitoAuthConfig={cognitoAuthConfig}>
              <EarlyAuthLoading />
              <AppContent router={router} />
            </AuthProvider>
          </App>
        </ConfigProvider>
      </SWRConfig>
    </ErrorBoundary>
    // </StrictMode>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");
const root = createRoot(rootElement, getSentryReactRootOptions());

import("./routers/index.tsx").then(({ default: router }) => {
  root.render(<AppRoot router={router} />);
});
